'use client'

import { useCallback } from 'react'

import {
  trackThenNavigate,
  withLockAbAfSubs,
} from '@/app/(makers)/modoocar/_features/modoocar-lock-ab'
import { useLockAb } from '@/app/(makers)/modoocar/_features/modoocar-lock-ab/_hooks/useLockAb'
import {
  getCurrentPagePath,
  useSeoOnelinkUrl,
  withWebshowAfDp,
} from '@/shared/external/onelink'

import type { TLockAbAfSub2 } from '@/app/(makers)/modoocar/_features/modoocar-lock-ab'

/** 데스크탑(lg) 기준 — 새 탭 랜딩. 모바일은 location.href여야 iOS Universal Link가 앱으로 넘어간다 */
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)'

interface IUseModoocarAppLinkParams {
  /** OneLink 기본 URL (검색 유입 SmartScript OneLink가 있으면 그것을 우선) */
  fallbackUrl: string
  /** af_sub2 — 앱 유도 지면 코드 */
  afSub2: TLockAbAfSub2
  /** 앱 설치 시 같은 상세 화면을 여는 webshow 딥링크(af_dp)를 싣는다 (기본 true) */
  withDeepLink?: boolean
}

/**
 * 모두의 차고 상세 앱 유도 링크 이동기.
 * OneLink(검색 유입 SmartScript 우선) + webshow 딥링크 + af_sub1(변형)·af_sub2(지면)를 조립하고,
 * 모바일은 클릭 이벤트 전송을 기다린 뒤 이동한다(이동이 먼저면 이벤트 유실).
 * 데스크탑은 현재 페이지가 남으므로 새 탭을 바로 연다(팝업 차단 회피).
 */
export const useModoocarAppLink = ({
  fallbackUrl,
  afSub2,
  withDeepLink = true,
}: IUseModoocarAppLinkParams) => {
  const onelinkUrl = useSeoOnelinkUrl(fallbackUrl)
  const {
    data: { afSub1 },
  } = useLockAb()

  return useCallback(
    (track: () => Promise<unknown> | void) => {
      const baseUrl = withDeepLink
        ? withWebshowAfDp(onelinkUrl, getCurrentPagePath())
        : onelinkUrl
      const url = withLockAbAfSubs(baseUrl, { afSub1, afSub2 })

      if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
        void track()
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }

      void trackThenNavigate(track, () => {
        window.location.href = url
      })
    },
    [onelinkUrl, afSub1, afSub2, withDeepLink]
  )
}
